// @ts-nocheck
import { useState, useEffect, useContext, useRef } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import {
  IconBriefcase,
  IconFileDescription,
  IconFileInvoice,
  IconCalendar,
  IconSearch,
  IconX,
} from '@tabler/icons-react';
import Fetch from '@/utils/Fetch';
import Helper from '@/utils/helper';
import Time from '@/utils/time';
import PageContext from '@/utils/page-context';
import CompanyService from '@/services/company-service';
import { useCustomerZoneCompany } from '@/utils/useCustomerZoneCompany';
import styles from './CustomerZoneHome.module.css';
import CustomerZoneHero from './CustomerZoneHero';
import CustomerZoneJobsTab from './CustomerZoneJobsTab';
import CustomerZoneQuotesTab from './CustomerZoneQuotesTab';
import CustomerZoneInvoicesTab from './CustomerZoneInvoicesTab';
import CustomerZoneAppointmentsTab from './CustomerZoneAppointmentsTab';
import CustomerZoneRail from './CustomerZoneRail';
import CustomerZoneFooter from './CustomerZoneFooter';

const TABS = [
  { key: 'jobs',         label: 'Jobs',         noun: 'jobs',         Icon: IconBriefcase },
  { key: 'quotes',       label: 'Quotes',       noun: 'quotes',       Icon: IconFileDescription },
  { key: 'invoices',     label: 'Invoices',     noun: 'invoices',     Icon: IconFileInvoice },
  { key: 'appointments', label: 'Appointments', noun: 'appointments', Icon: IconCalendar },
];

const LIMIT = 4;
const PAGE_SIZE = 6;

function pageList(current: number, total: number): (number | '…')[] {
  const keep = new Set([1, total, current - 1, current, current + 1]);
  const out: (number | '…')[] = [];
  for (let n = 1; n <= total; n++) {
    if (keep.has(n)) out.push(n);
    else if (out[out.length - 1] !== '…') out.push('…');
  }
  return out;
}

function matchesSearch(item: any, query: string, fields: ((x: any) => string)[]) {
  const q = query.toLowerCase();
  return fields.some(f => (f(item) || '').toLowerCase().includes(q));
}

const SEARCH_FIELDS: Record<string, ((x: any) => string)[]> = {
  jobs:         [(j) => j.JobCardNumber, (j) => j.Location?.AddressLine1 || ''],
  quotes:       [(q) => q.QuoteNumber,   (q) => q.ItemReference || ''],
  invoices:     [(v) => v.InvoiceNumber, (v) => v.ItemReference || ''],
  appointments: [(a) => a.Subject || '', (a) => a.Location?.AddressLine1 || ''],
};

const SEARCH_PLACEHOLDER: Record<string, string> = {
  jobs:         'Search jobs…',
  quotes:       'Search quotes…',
  invoices:     'Search invoices…',
  appointments: 'Search appointments…',
};

export default function CustomerZoneHome() {
  const router = useRouter();
  const { t, c, tab, expand } = router.query;
  const pageContext = useContext(PageContext) as any;
  const [activeTab, setActiveTab] = useState('jobs');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [page, setPage] = useState(1);
  const mainCardRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const valid = TABS.map(x => x.key);
    if (tab && valid.includes(tab as string)) setActiveTab(tab as string);
  }, [tab]);

  useEffect(() => {
    if (expand === '1') { setExpanded(true); setPage(1); }
  }, [expand]);

  const switchTab = (key: string) => {
    setActiveTab(key);
    setSearch('');
    setPage(1);
  };

  const goToPage = (n: number) => {
    setPage(n);
    mainCardRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };

  // ── Data fetching ────────────────────────────────────────────────────────────

  const { data: tenantData } = useQuery(
    ['czTenant', t],
    () => Fetch.preLogin({ tenantID: t }),
    { enabled: !!t }
  );
  const tenant = tenantData?.HttpStatusCode === 200 && tenantData.Results?.length > 0
    ? tenantData.Results[0] : null;

  useEffect(() => {
    if (tenantData && !tenant) router.replace('/');
  }, [tenantData]);

  const { data: company } = useCustomerZoneCompany(t, c, tenant?.API);

  const { data: now } = useQuery(
    ['czNow', t, c],
    () => Time.nowCZ(t, c, tenant.API),
    { enabled: !!tenant }
  );
  const todayFilter = now ? Time.today(now) : null;

  const { data: currencySymbol = '' } = useQuery(
    ['czCurrency', t, c],
    () => CompanyService.getCustomerZoneCurrencySymbol(t, c, tenant.API),
    { enabled: !!tenant }
  );

  const { data: openJobsData } = useQuery(
    ['czHomeOpenJobs', t, c],
    () => Fetch.post({ url: '/CustomerZone/GetJobs', params: { PageSize: 8, SortExpression: 'StartDate', SortDirection: 'descending', OnlyClosed: false }, tenantID: t, customerID: c, apiUrlOverride: tenant.API }),
    { enabled: !!tenant }
  );

  const { data: closedJobsData } = useQuery(
    ['czHomeClosedJobs', t, c],
    () => Fetch.post({ url: '/CustomerZone/GetJobs', params: { PageSize: 8, SortExpression: 'StartDate', SortDirection: 'descending', OnlyClosed: true }, tenantID: t, customerID: c, apiUrlOverride: tenant.API }),
    { enabled: !!tenant }
  );

  const { data: quotesData } = useQuery(
    ['czHomeQuotes', t, c],
    () => Fetch.post({ url: '/CustomerZone/GetQuotes', params: { pageSize: 12, pageIndex: 0, PopulatedList: false, SortExpression: 'QuoteDate', SortDirection: 'descending', QuoteStatusIDList: ['Approved', 'Accepted', 'Declined', 'Invoiced', 'Expired', 'Cancelled'], IncludeClosed: true }, tenantID: t, customerID: c, apiUrlOverride: tenant.API }),
    { enabled: !!tenant }
  );

  const { data: invoicesData } = useQuery(
    ['czHomeInvoices', t, c],
    () => Fetch.post({ url: '/CustomerZone/GetInvoices', params: { PageSize: 12, SortExpression: 'StartDate', SortDirection: 'descending', PopulatedList: false, InvoiceStatusIDList: ['Unpaid', 'Overdue', 'Paid'], IncludeClosed: true }, tenantID: t, customerID: c, apiUrlOverride: tenant.API }),
    { enabled: !!tenant }
  );

  const { data: appointmentsData } = useQuery(
    ['czHomeAppointments', t, c, todayFilter],
    () => Fetch.post({ url: '/CustomerZone/GetAppointments', params: { PageSize: 12, SortExpression: 'StartDateTime', SortDirection: 'ascending', StartDateTime: todayFilter }, tenantID: t, customerID: c, apiUrlOverride: tenant.API }),
    { enabled: !!tenant && !!todayFilter }
  );

  useEffect(() => {
    const name = openJobsData?.headerTenantName || quotesData?.headerTenantName || tenant?.Name;
    if (name) pageContext?.updateTenantName?.(name);
  }, [openJobsData, quotesData, tenant]);

  if (!tenant) return null;

  // ── Derived values ───────────────────────────────────────────────────────────

  const companyName = company?.Name || pageContext?.tenantName || tenant?.Name || '';
  const logoUrl     = company?.LogoUrl || null;
  const rawPhone    = company?.WhatsAppNumber || company?.TelephoneNumber || null;
  const waPhone     = rawPhone ? rawPhone.replace(/\D/g, '').replace(/^0/, '27') : null;

  const openJobs: any[]    = (openJobsData?.Results || []).map((j: any) => ({ ...j, _closed: false }));
  const closedJobs: any[]  = (closedJobsData?.Results || []).map((j: any) => ({ ...j, _closed: true }));
  const allJobs            = [...openJobs, ...closedJobs];
  const quotes: any[]      = quotesData?.Results || [];
  const invoices: any[]    = invoicesData?.Results || [];
  const appointments: any[]= appointmentsData?.Results || [];
  const nextAppt           = appointments[0] || null;

  const nav  = (path: string) => Helper.nextRouter(router.push, path);
  const base = `?t=${t}&c=${c}`;

  const customerFirstName = (() => {
    const name = openJobs[0]?.CustomerName || quotes[0]?.CustomerName ||
                 invoices[0]?.CustomerName || appointments[0]?.CustomerName || '';
    return name.split(' ')[0];
  })();

  // ── Search filtering ─────────────────────────────────────────────────────────

  const allItems: Record<string, any[]> = { jobs: allJobs, quotes, invoices, appointments };
  const filtered: Record<string, any[]> = search
    ? {
        jobs:         allJobs.filter(x => matchesSearch(x, search, SEARCH_FIELDS.jobs)),
        quotes:       quotes.filter(x => matchesSearch(x, search, SEARCH_FIELDS.quotes)),
        invoices:     invoices.filter(x => matchesSearch(x, search, SEARCH_FIELDS.invoices)),
        appointments: appointments.filter(x => matchesSearch(x, search, SEARCH_FIELDS.appointments)),
      }
    : allItems;

  const activeItems   = filtered[activeTab] || [];
  const totalCount    = allItems[activeTab]?.length || 0;
  const hasSearch     = !!search;
  const isPaged       = hasSearch || expanded;
  const totalPages    = isPaged ? Math.max(1, Math.ceil(activeItems.length / PAGE_SIZE)) : 1;
  const currentPage   = Math.min(Math.max(1, page), totalPages);
  const start         = (currentPage - 1) * PAGE_SIZE;
  const visibleItems  = isPaged ? activeItems.slice(start, start + PAGE_SIZE) : activeItems.slice(0, LIMIT);
  const showFooter    = !hasSearch && totalCount > LIMIT;
  const showPager     = isPaged && totalPages > 1;
  const activeTabCfg  = TABS.find(x => x.key === activeTab)!;

  const TAB_CONTENT = {
    jobs:         <CustomerZoneJobsTab jobs={activeTab === 'jobs' ? visibleItems : []} now={now} base={base} nav={nav} />,
    quotes:       <CustomerZoneQuotesTab quotes={activeTab === 'quotes' ? visibleItems : []} currencySymbol={currencySymbol} base={base} nav={nav} />,
    invoices:     <CustomerZoneInvoicesTab invoices={activeTab === 'invoices' ? visibleItems : []} currencySymbol={currencySymbol} base={base} nav={nav} />,
    appointments: <CustomerZoneAppointmentsTab appointments={activeTab === 'appointments' ? visibleItems : []} base={base} nav={nav} />,
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className={styles.root}>

      <CustomerZoneHero companyName={companyName} logoUrl={logoUrl} expanded={expanded} />

      <div className={styles.shell}>

        <div className={styles.body}>

          {customerFirstName && (
            <h1 className={`${styles.welcome}${expanded ? ` ${styles.welcomeCollapsed}` : ''}`}>Welcome, {customerFirstName}</h1>
          )}

          {/* Left column: tabbed card */}
          <section>
          {/* Main tabbed card */}
          <main className={styles.mainCard} ref={mainCardRef}>
            <div className={styles.tabs} role="tablist">
              {TABS.map(({ key, label, Icon }) => (
                <button
                  key={key}
                  className={`${styles.tab}${activeTab === key ? ` ${styles.tabActive}` : ''}`}
                  role="tab"
                  aria-selected={activeTab === key}
                  onClick={() => switchTab(key)}
                >
                  <span className={styles.tabIcon}><Icon size={16} /></span>
                  <span>{label}</span>
                </button>
              ))}
            </div>

            {/* Search bar */}
            <div className={styles.searchOuter}>
            <div className={styles.searchBar}>
              <span className={styles.searchIcon}><IconSearch size={15} /></span>
              <input
                className={styles.searchInput}
                type="text"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                placeholder={SEARCH_PLACEHOLDER[activeTab]}
                aria-label={SEARCH_PLACEHOLDER[activeTab]}
              />
              {search && (
                <button
                  className={styles.searchClear}
                  type="button"
                  onClick={() => setSearch('')}
                  aria-label="Clear search"
                >
                  <IconX size={14} />
                </button>
              )}
            </div>
            </div>

            <div className={styles.cardBody}>
              {hasSearch && !activeItems.length ? (
                <p className={styles.emptyNote}>No {activeTabCfg.noun} found for &ldquo;{search}&rdquo;</p>
              ) : (
                <div className={styles.grid}>
                  {TAB_CONTENT[activeTab]}
                </div>
              )}
            </div>

            {(showFooter || showPager) && (
              <div className={`${styles.cardFoot}${isPaged ? ` ${styles.cardFootPaged}` : ''}`}>
                {isPaged && (
                  <span className={styles.pgInfo}>
                    {start + 1}–{start + visibleItems.length} of {activeItems.length}
                  </span>
                )}
                {showPager && (
                  <nav className={styles.pager} aria-label="Pagination">
                    <button
                      className={styles.pgStep}
                      type="button"
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      aria-label="Previous page"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
                    </button>
                    {pageList(currentPage, totalPages).map((n, idx) =>
                      n === '…'
                        ? <span key={`gap-${idx}`} className={styles.pgGap}>…</span>
                        : <button
                            key={n}
                            className={`${styles.pgNum}${n === currentPage ? ` ${styles.pgNumActive}` : ''}`}
                            type="button"
                            onClick={() => goToPage(n as number)}
                            aria-current={n === currentPage ? 'page' : undefined}
                          >{n}</button>
                    )}
                    <button
                      className={styles.pgStep}
                      type="button"
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      aria-label="Next page"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                    </button>
                  </nav>
                )}
                {showFooter && (
                  <button
                    className={styles.viewAllBtn}
                    type="button"
                    onClick={() => { setExpanded(e => !e); setPage(1); }}
                    aria-expanded={expanded}
                  >
                    {expanded ? 'Show less ↑' : `View all ${activeTabCfg.noun} (${totalCount}) ↓`}
                  </button>
                )}
              </div>
            )}
          </main>
          </section>

          <CustomerZoneRail
            nextAppt={nextAppt}
            companyName={companyName}
            waPhone={waPhone}
            base={base}
            nav={nav}
          />
        </div>

      </div>

      <CustomerZoneFooter />

    </div>
  );
}
